<script>
import { getUnixTime } from 'date-fns';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import { emitter } from 'shared/helpers/mitt';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_BULK_ACTION_SNOOZE_CONVERSATION,
  CMD_BULK_ACTION_REOPEN_CONVERSATION,
  CMD_BULK_ACTION_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import AgentSelector from './AgentSelector.vue';
import UpdateActions from './UpdateActions.vue';
import LabelActions from './LabelActions.vue';
import TeamActions from './TeamActions.vue';
import CustomSnoozeModal from 'dashboard/components/CustomSnoozeModal.vue';
export default {
  components: {
    AgentSelector,
    UpdateActions,
    LabelActions,
    TeamActions,
    CustomSnoozeModal,
  },
  props: {
    conversations: {
      type: Array,
      default: () => [],
    },
    allConversationsSelected: {
      type: Boolean,
      default: false,
    },
    selectedInboxes: {
      type: Array,
      default: () => [],
    },
    showOpenAction: {
      type: Boolean,
      default: false,
    },
    showResolvedAction: {
      type: Boolean,
      default: false,
    },
    showSnoozedAction: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'selectAllConversations',
    'assignAgent',
    'updateConversations',
    'assignLabels',
    'assignTeam',
    'resolveConversations',
  ],
  data() {
    return {
      showAgentsList: false,
      showUpdateActions: false,
      showLabelActions: false,
      showTeamsList: false,
      showCustomTimeSnoozeModal: false,
    };
  },
  computed: {
    // Qual painel está aberto — o botão dele fica aceso. Um painel por vez:
    // antes dava para abrir os quatro empilhados.
    painelAberto() {
      if (this.showLabelActions) return 'labels';
      if (this.showUpdateActions) return 'status';
      if (this.showAgentsList) return 'agent';
      if (this.showTeamsList) return 'team';
      return '';
    },
    acoes() {
      return [
        {
          id: 'labels',
          icone: 'i-lucide-tags',
          tom: 'advup-acao--t-azul',
          rotulo: this.$t('BULK_ACTION.LABELS.ASSIGN_LABELS'),
        },
        {
          id: 'status',
          icone: 'i-lucide-circle-check',
          tom: 'advup-acao--t-iris',
          rotulo: this.$t('BULK_ACTION.UPDATE.CHANGE_STATUS'),
        },
        {
          id: 'agent',
          icone: 'i-lucide-user-round-plus',
          tom: 'advup-acao--t-teal',
          rotulo: this.$t('BULK_ACTION.ASSIGN_AGENT_TOOLTIP'),
        },
        {
          id: 'team',
          icone: 'i-lucide-users-round',
          tom: 'advup-acao--t-teal',
          rotulo: this.$t('BULK_ACTION.ASSIGN_TEAM_TOOLTIP'),
        },
      ];
    },
  },
  mounted() {
    emitter.on(
      CMD_BULK_ACTION_SNOOZE_CONVERSATION,
      this.onCmdSnoozeConversation
    );
    emitter.on(
      CMD_BULK_ACTION_REOPEN_CONVERSATION,
      this.onCmdReopenConversation
    );
    emitter.on(
      CMD_BULK_ACTION_RESOLVE_CONVERSATION,
      this.onCmdResolveConversation
    );
  },
  unmounted() {
    emitter.off(
      CMD_BULK_ACTION_SNOOZE_CONVERSATION,
      this.onCmdSnoozeConversation
    );
    emitter.off(
      CMD_BULK_ACTION_REOPEN_CONVERSATION,
      this.onCmdReopenConversation
    );
    emitter.off(
      CMD_BULK_ACTION_RESOLVE_CONVERSATION,
      this.onCmdResolveConversation
    );
  },
  methods: {
    onCmdSnoozeConversation(snoozeType) {
      if (snoozeType === wootConstants.SNOOZE_OPTIONS.UNTIL_CUSTOM_TIME) {
        this.showCustomTimeSnoozeModal = true;
      } else if (typeof snoozeType === 'number') {
        this.updateConversations('snoozed', snoozeType);
      } else {
        this.updateConversations('snoozed', findSnoozeTime(snoozeType) || null);
      }
    },
    onCmdReopenConversation() {
      this.updateConversations('open', null);
    },
    onCmdResolveConversation() {
      this.updateConversations('resolved', null);
    },
    customSnoozeTime(customSnoozedTime) {
      this.showCustomTimeSnoozeModal = false;
      if (customSnoozedTime) {
        this.updateConversations('snoozed', getUnixTime(customSnoozedTime));
      }
    },
    hideCustomSnoozeModal() {
      this.showCustomTimeSnoozeModal = false;
    },
    selectAll(e) {
      this.$emit('selectAllConversations', e.target.checked);
    },
    submit(agent) {
      this.$emit('assignAgent', agent);
    },
    updateConversations(status, snoozedUntil) {
      this.$emit('updateConversations', status, snoozedUntil);
    },
    assignLabels(labels) {
      this.$emit('assignLabels', labels);
    },
    assignTeam(team) {
      this.$emit('assignTeam', team);
    },
    resolveConversations() {
      this.$emit('resolveConversations');
    },
    alternarPainel(id) {
      const aberto = this.painelAberto === id;
      this.fecharPaineis();
      if (aberto) return;
      this.showLabelActions = id === 'labels';
      this.showUpdateActions = id === 'status';
      this.showAgentsList = id === 'agent';
      this.showTeamsList = id === 'team';
    },
    fecharPaineis() {
      this.showLabelActions = false;
      this.showUpdateActions = false;
      this.showAgentsList = false;
      this.showTeamsList = false;
    },
  },
};
</script>

<template>
  <div class="bka">
    <div class="bka__barra">
      <label class="bka__todas">
        <input
          type="checkbox"
          class="advup-caixa"
          :checked="allConversationsSelected"
          :indeterminate.prop="!allConversationsSelected"
          :aria-label="$t('BULK_ACTION.SELECT_ALL')"
          @change="selectAll($event)"
        />
        <span class="bka__conta">
          <span :key="conversations.length" class="bka__n">
            {{
              $t(
                'BULK_ACTION.CONVERSATIONS_SELECTED',
                { conversationCount: conversations.length },
                conversations.length
              )
            }}
          </span>
        </span>
      </label>
      <div class="bka__acoes">
        <!-- O `data-bka` marca os botões para o clique-fora dos painéis:
             sem isso, clicar no botão do painel aberto fechava (clique fora)
             e reabria (o próprio clique). -->
        <button
          v-for="acao in acoes"
          :key="acao.id"
          v-tooltip.top="acao.rotulo"
          type="button"
          class="advup-acao advup-acao--tingida"
          :class="[acao.tom, { 'advup-acao--aceso': painelAberto === acao.id }]"
          :aria-label="acao.rotulo"
          :aria-expanded="painelAberto === acao.id"
          data-bka
          @click="alternarPainel(acao.id)"
        >
          <i :class="acao.icone" />
        </button>
      </div>
    </div>

    <LabelActions
      v-if="showLabelActions"
      @assign="assignLabels"
      @close="fecharPaineis"
    />
    <UpdateActions
      v-if="showUpdateActions"
      :show-resolve="!showResolvedAction"
      :show-reopen="!showOpenAction"
      :show-snooze="!showSnoozedAction"
      @update="updateConversations"
      @close="fecharPaineis"
    />
    <AgentSelector
      v-if="showAgentsList"
      :selected-inboxes="selectedInboxes"
      :conversation-count="conversations.length"
      @select="submit"
      @close="fecharPaineis"
    />
    <TeamActions
      v-if="showTeamsList"
      @assign-team="assignTeam"
      @close="fecharPaineis"
    />

    <p v-if="allConversationsSelected" class="bka__aviso">
      <i class="i-lucide-info" aria-hidden="true" />
      {{ $t('BULK_ACTION.ALL_CONVERSATIONS_SELECTED_ALERT') }}
    </p>
    <woot-modal
      v-model:show="showCustomTimeSnoozeModal"
      :on-close="hideCustomSnoozeModal"
    >
      <CustomSnoozeModal
        @close="hideCustomSnoozeModal"
        @choose-time="customSnoozeTime"
      />
    </woot-modal>
  </div>
</template>

<style scoped>
/* A barra das ações em massa: aparece no alto da lista quando há conversas
   marcadas. Véu azul claro — é um MODO da lista, e precisa parecer um.
   Entra e sai pela altura: quem anima é o `<Transition name="advup-altura">` do
   ChatList (24/10/2026). Uma animação própria aqui venceria a da peça por
   especificidade e a saída não rodaria. */
.bka {
  position: relative;
  padding: var(--esp-2) var(--esp-3);
  border-bottom: 1px solid rgb(var(--blue-5));
  background: rgb(var(--blue-2));
}

.dark .bka {
  border-color: rgb(var(--blue-6));
  background: rgb(var(--blue-3));
}

.bka__barra {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
}

.bka__todas {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  min-width: 0;
  cursor: pointer;
}

.bka__conta {
  overflow: hidden;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* Marcar mais uma: a frase sobe no lugar (24/10/2026) — a contagem mudou. */
.bka__n {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  animation: bka-n var(--mov-2) var(--mov-curva);
}

@keyframes bka-n {
  from {
    translate: 0 4px;
    opacity: 0;
  }
}

.bka__acoes {
  display: flex;
  flex-shrink: 0;
  gap: var(--esp-1);
}

.bka__aviso {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: var(--esp-2) 0 0;
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-2);
  background: rgb(var(--amber-3));
  font-size: var(--txt-nota);
  color: color-mix(in srgb, rgb(var(--amber-12)) 18%, rgb(var(--amber-11)));
}

.bka__aviso > i {
  flex-shrink: 0;
  font-size: var(--ico-1);
}

@media (prefers-reduced-motion: reduce) {
  .bka__n {
    animation: none;
  }
}
</style>
